export const APP_PROMPT_PREFIX = 'Build a ';

export const APP_PROMPT_IDEAS = [
  'client portal with billing',
  'booking platform for a wellness studio',
  'live analytics dashboard for a growing team',
  'marketplace for independent creators',
  'project tracker for a remote team',
  'membership hub with paid subscriptions',
  'property management portal for landlords',
  'learning platform with student progress',
  'inventory system for a local retailer',
  'customer support knowledge base',
  'event planning workspace for agencies',
  'restaurant ordering and reservation app',
  'applicant tracker for a hiring team',
  'personal finance dashboard with budgets',
] as const;

export const DEFAULT_APP_PROMPT = `${APP_PROMPT_PREFIX}${APP_PROMPT_IDEAS[0]}`;

/**
 * Run the shared prompt-placeholder typewriter without causing React renders.
 * It pauses while the textarea is focused or contains user input, and respects
 * the user's reduced-motion preference.
 */
export function attachPromptPlaceholderTypewriter(textarea: HTMLTextAreaElement): () => void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    textarea.placeholder = DEFAULT_APP_PROMPT;
    return () => {};
  }

  let timer = 0;
  let ideaIndex = 0;
  let characterIndex = APP_PROMPT_IDEAS[0].length;
  let deleting = true;
  let focused = false;
  let disposed = false;

  const writePlaceholder = (): void => {
    const idea = APP_PROMPT_IDEAS[ideaIndex] ?? APP_PROMPT_IDEAS[0];
    textarea.placeholder = `${APP_PROMPT_PREFIX}${idea.slice(0, characterIndex)}`;
  };

  const schedule = (delay: number): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(tick, delay);
  };

  const tick = (): void => {
    if (disposed || focused || textarea.value) return;

    const idea = APP_PROMPT_IDEAS[ideaIndex] ?? APP_PROMPT_IDEAS[0];
    if (deleting) {
      characterIndex = Math.max(0, characterIndex - 1);
      writePlaceholder();
      if (characterIndex === 0) {
        deleting = false;
        ideaIndex = (ideaIndex + 1) % APP_PROMPT_IDEAS.length;
        schedule(280);
      } else {
        schedule(22);
      }
      return;
    }

    characterIndex = Math.min(idea.length, characterIndex + 1);
    writePlaceholder();
    if (characterIndex === idea.length) {
      deleting = true;
      schedule(1900);
    } else {
      schedule(42);
    }
  };

  const handleFocus = (): void => {
    focused = true;
    window.clearTimeout(timer);
    characterIndex = (APP_PROMPT_IDEAS[ideaIndex] ?? APP_PROMPT_IDEAS[0]).length;
    writePlaceholder();
  };

  const handleBlur = (): void => {
    focused = false;
    if (!textarea.value) schedule(900);
  };

  const handleInput = (): void => {
    window.clearTimeout(timer);
    if (!textarea.value && !focused) schedule(650);
  };

  textarea.addEventListener('focus', handleFocus);
  textarea.addEventListener('blur', handleBlur);
  textarea.addEventListener('input', handleInput);
  writePlaceholder();
  schedule(1600);

  return () => {
    disposed = true;
    window.clearTimeout(timer);
    textarea.removeEventListener('focus', handleFocus);
    textarea.removeEventListener('blur', handleBlur);
    textarea.removeEventListener('input', handleInput);
  };
}
